import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categoryData, categoryTabs } from '@/mocks/categoryData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'football');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = categoryData[activeSlug as keyof typeof categoryData] || categoryData.football;

  const handleTabChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.REACT_APP_NAVIGATE(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src={currentCategory.bannerImage}
            alt={currentCategory.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-background-50/15 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
                <i className={`${currentCategory.icon} text-3xl md:text-4xl text-background-50`}></i>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3 tracking-tight">
                {currentCategory.name}
              </h1>
              <p className="text-sm md:text-base text-background-200/90 max-w-lg mx-auto">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </section>

        <Breadcrumb items={[
          { label: '体育赛事', href: '/category/sports' },
          { label: currentCategory.name },
        ]} />

        <section className="w-full bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-4">
            <div className="flex flex-wrap items-center gap-2 pb-6 border-b border-background-200">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.slug}
                  onClick={() => handleTabChange(tab.slug)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === tab.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${tab.icon} text-xs`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 pb-14 md:pb-20">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                近期赛事
              </h2>
              <span className="text-xs text-foreground-400">
                共 {currentCategory.matches.length} 场
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {currentCategory.leagues.map((league) => (
                <span
                  key={league}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border border-background-200 whitespace-nowrap"
                >
                  {league}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {currentCategory.matches.map((match: any) => (
                <a
                  key={match.id}
                  href={`/article/${match.id}`}
                  className="group flex items-center justify-between bg-background-50 border border-background-200 rounded-xl p-5 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-foreground-500">{match.league}</span>
                      <span className="text-xs text-foreground-400">
                        <i className="ri-time-line mr-0.5"></i>{match.time}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-foreground-900 truncate">{match.homeTeam}</span>
                      <span className="text-xs text-foreground-400 shrink-0">vs</span>
                      <span className="text-sm font-bold text-foreground-900 truncate">{match.awayTeam}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    {match.oddsDraw ? (
                      <>
                        <div className="text-center min-w-[44px]">
                          <span className="block text-[10px] text-foreground-500 mb-0.5">主胜</span>
                          <span className="block text-sm font-bold text-primary-600">{match.oddsHome}</span>
                        </div>
                        <div className="text-center min-w-[44px]">
                          <span className="block text-[10px] text-foreground-500 mb-0.5">平</span>
                          <span className="block text-sm font-bold text-foreground-600">{match.oddsDraw}</span>
                        </div>
                        <div className="text-center min-w-[44px]">
                          <span className="block text-[10px] text-foreground-500 mb-0.5">客胜</span>
                          <span className="block text-sm font-bold text-foreground-600">{match.oddsAway}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-center min-w-[44px]">
                          <span className="block text-[10px] text-foreground-500 mb-0.5">{match.homeTeam}</span>
                          <span className="block text-sm font-bold text-primary-600">{match.oddsHome}</span>
                        </div>
                        <div className="text-center min-w-[44px]">
                          <span className="block text-[10px] text-foreground-500 mb-0.5">{match.awayTeam}</span>
                          <span className="block text-sm font-bold text-foreground-600">{match.oddsAway}</span>
                        </div>
                      </>
                    )}
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}